import React, { useEffect, useState } from 'react';
import { AlertTriangle, BarChart3, CheckCircle2, Gauge, RefreshCw, Users } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';
import { MAX_CONTRACTORS_PER_LEAD } from '@/lib/marketplace';

/**
 * ---------------------------------------------------------------------------
 * ADMIN: LEAD CAPACITY & CREDIT REPORTING
 * ---------------------------------------------------------------------------
 * Every number here is read straight from the tables the database itself
 * maintains inside the locked claim transaction — this screen never recomputes
 * business rules, it only reports them:
 *
 *   mkt_listings.contractor_count / max_contractors / status  -> capacity
 *   mkt_admin_alerts (alert_type='lead_sold_out')             -> sold-out alerts
 *   mkt_credit_ledger                                         -> credits used
 *   mkt_subscriptions + mkt_profiles.extra_credits            -> credits left
 *
 * This is internal analytics, so it is the ONE place allowed to surface lead
 * age-adjacent data; nothing here is ever rendered to a contractor.
 */

const COPY = {
  en: {
    title: 'Lead capacity & credits',
    sub: `Every lead is shared with at most ${MAX_CONTRACTORS_PER_LEAD} contractors. Capacity, sold-out status and credit deductions are all enforced in the database.`,
    refresh: 'Refresh',
    open: 'Open leads',
    soldOut: 'Sold out leads',
    nearing: `Nearing capacity (${MAX_CONTRACTORS_PER_LEAD - 1} of ${MAX_CONTRACTORS_PER_LEAD})`,
    avg: 'Avg. contractors per lead',
    creditsUsed: 'Total credits used',
    creditsLeft: 'Credits remaining (all members)',
    alertsTitle: 'Sold-out alerts',
    alertsEmpty: 'No lead has reached capacity yet.',
    nearingTitle: 'Leads about to sell out',
    nearingEmpty: 'No lead is one slot away from capacity.',
    membersTitle: 'Remaining credits by member',
    membersEmpty: 'No active memberships yet.',
    ack: 'Acknowledge',
    acked: 'Acknowledged',
    plan: 'Plan',
    cycle: 'Cycle credits',
    extra: 'Single-lead credits',
    slots: 'Slots',
  },
  es: {
    title: 'Capacidad de leads y créditos',
    sub: `Cada lead se comparte con un máximo de ${MAX_CONTRACTORS_PER_LEAD} contratistas. La capacidad, el estado agotado y el descuento de créditos se aplican en la base de datos.`,
    refresh: 'Actualizar',
    open: 'Leads abiertos',
    soldOut: 'Leads agotados',
    nearing: `Casi agotados (${MAX_CONTRACTORS_PER_LEAD - 1} de ${MAX_CONTRACTORS_PER_LEAD})`,
    avg: 'Promedio de contratistas por lead',
    creditsUsed: 'Créditos usados en total',
    creditsLeft: 'Créditos disponibles (todos los miembros)',
    alertsTitle: 'Alertas de leads agotados',
    alertsEmpty: 'Ningún lead ha llegado a su capacidad.',
    nearingTitle: 'Leads a punto de agotarse',
    nearingEmpty: 'Ningún lead está a un cupo de agotarse.',
    membersTitle: 'Créditos restantes por miembro',
    membersEmpty: 'Todavía no hay membresías activas.',
    ack: 'Marcar visto',
    acked: 'Visto',
    plan: 'Plan',
    cycle: 'Créditos del ciclo',
    extra: 'Créditos individuales',
    slots: 'Cupos',
  },
};

interface ListingRow {
  id: string;
  title: string;
  trade: string;
  state: string | null;
  status: string;
  contractor_count: number | null;
  max_contractors: number | null;
}

interface AlertRow {
  id: string;
  title: string;
  body: string | null;
  acknowledged: boolean;
  created_at: string;
}

interface MemberRow {
  userId: string;
  label: string;
  planCode: string;
  remaining: number;
  extra: number;
}

const Stat: React.FC<{ label: string; value: string | number; tone?: string; icon: React.ReactNode }> = ({
  label,
  value,
  tone = 'text-slate-900',
  icon,
}) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5">
    <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-400">
      {icon} {label}
    </p>
    <p className={`mt-2 text-3xl font-extrabold leading-none ${tone}`}>{value}</p>
  </div>
);

const AdminLeadCredits: React.FC = () => {
  const { lang } = useI18n();
  const c = COPY[lang === 'es' ? 'es' : 'en'];

  const [listings, setListings] = useState<ListingRow[]>([]);
  const [alerts, setAlerts] = useState<AlertRow[]>([]);
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [creditsUsed, setCreditsUsed] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);

    const [listRes, alertRes, subRes, ledgerRes] = await Promise.all([
      supabase
        .from('mkt_listings')
        .select('id,title,trade,state,status,contractor_count,max_contractors'),
      supabase
        .from('mkt_admin_alerts')
        .select('id,title,body,acknowledged,created_at')
        .eq('alert_type', 'lead_sold_out')
        .order('created_at', { ascending: false })
        .limit(25),
      supabase
        .from('mkt_subscriptions')
        .select('user_id,plan_code,included_leads,leads_used')
        .in('status', ['active', 'past_due']),
      supabase.from('mkt_credit_ledger').select('delta,kind'),
    ]);

    const rows = (listRes.data || []) as ListingRow[];
    setListings(rows);
    setAlerts((alertRes.data || []) as AlertRow[]);

    // "Total credits used" = every spend entry in the ledger (plan + single-lead).
    const spends = (ledgerRes.data || []).filter(
      (r: { kind: string }) => r.kind === 'plan_spend' || r.kind === 'extra_spend',
    );
    setCreditsUsed(spends.length);

    const subs = (subRes.data || []) as {
      user_id: string;
      plan_code: string;
      included_leads: number;
      leads_used: number;
    }[];

    if (subs.length) {
      const { data: profs } = await supabase
        .from('mkt_profiles')
        .select('id,email,company_name,extra_credits')
        .in('id', subs.map((s) => s.user_id));

      setMembers(
        subs.map((s) => {
          const p = (profs || []).find((x: { id: string }) => x.id === s.user_id);
          return {
            userId: s.user_id,
            label: p?.company_name || p?.email || s.user_id.slice(0, 8),
            planCode: s.plan_code || '—',
            remaining: Math.max(0, (s.included_leads || 0) - (s.leads_used || 0)),
            extra: Math.max(0, p?.extra_credits || 0),
          };
        }),
      );
    } else {
      setMembers([]);
    }

    setBusy(false);
  };

  useEffect(() => {
    load();
  }, []);

  const acknowledge = async (id: string) => {
    await supabase
      .from('mkt_admin_alerts')
      .update({ acknowledged: true, acknowledged_at: new Date().toISOString() })
      .eq('id', id);
    setAlerts((a) => a.map((x) => (x.id === id ? { ...x, acknowledged: true } : x)));
  };

  const slotsOf = (l: ListingRow) => l.contractor_count || 0;
  const maxOf = (l: ListingRow) => l.max_contractors || MAX_CONTRACTORS_PER_LEAD;

  const soldOutRows = listings.filter((l) => l.status === 'sold_out' || slotsOf(l) >= maxOf(l));
  const openRows = listings.filter((l) => l.status === 'published' && slotsOf(l) < maxOf(l));
  const nearingRows = openRows.filter((l) => slotsOf(l) === maxOf(l) - 1);
  const avg = listings.length
    ? (listings.reduce((s, l) => s + slotsOf(l), 0) / listings.length).toFixed(2)
    : '0.00';
  const creditsLeft = members.reduce((s, m) => s + m.remaining + m.extra, 0);

  return (
    <section className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-extrabold text-slate-900">{c.title}</h2>
          <p className="mt-1 max-w-3xl text-sm text-slate-500">{c.sub}</p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${busy ? 'animate-spin' : ''}`} /> {c.refresh}
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Stat label={c.open} value={openRows.length} icon={<Users className="h-3.5 w-3.5" />} />
        <Stat
          label={c.soldOut}
          value={soldOutRows.length}
          tone="text-slate-500"
          icon={<CheckCircle2 className="h-3.5 w-3.5" />}
        />
        <Stat
          label={c.nearing}
          value={nearingRows.length}
          tone={nearingRows.length ? 'text-amber-600' : 'text-slate-900'}
          icon={<AlertTriangle className="h-3.5 w-3.5" />}
        />
        <Stat label={c.avg} value={avg} icon={<BarChart3 className="h-3.5 w-3.5" />} />
        <Stat label={c.creditsUsed} value={creditsUsed} icon={<Gauge className="h-3.5 w-3.5" />} />
        <Stat label={c.creditsLeft} value={creditsLeft} icon={<Gauge className="h-3.5 w-3.5" />} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <h3 className="text-sm font-bold uppercase tracking-wide text-slate-500">{c.alertsTitle}</h3>
          {alerts.length === 0 ? (
            <p className="mt-3 text-sm text-slate-500">{c.alertsEmpty}</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {alerts.map((a) => (
                <li key={a.id} className="rounded-xl border border-slate-200 p-3">
                  <p className="text-sm font-bold text-slate-900">{a.title}</p>
                  {a.body && <p className="mt-0.5 text-xs text-slate-500">{a.body}</p>}
                  <div className="mt-2">
                    {a.acknowledged ? (
                      <span className="text-[11px] font-bold uppercase tracking-wide text-emerald-600">
                        {c.acked}
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => acknowledge(a.id)}
                        className="rounded-lg bg-slate-900 px-3 py-1.5 text-[11px] font-bold text-white hover:bg-slate-700"
                      >
                        {c.ack}
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <h3 className="text-sm font-bold uppercase tracking-wide text-slate-500">{c.nearingTitle}</h3>
          {nearingRows.length === 0 ? (
            <p className="mt-3 text-sm text-slate-500">{c.nearingEmpty}</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {nearingRows.map((l) => (
                <li
                  key={l.id}
                  className="flex items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3"
                >
                  <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800">{l.title}</span>
                  <span className="shrink-0 text-xs font-bold text-amber-700">
                    {c.slots} {slotsOf(l)}/{maxOf(l)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="text-sm font-bold uppercase tracking-wide text-slate-500">{c.membersTitle}</h3>
        {members.length === 0 ? (
          <p className="mt-3 text-sm text-slate-500">{c.membersEmpty}</p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[520px] text-left text-sm">
              <thead>
                <tr className="text-[11px] font-bold uppercase tracking-wide text-slate-400">
                  <th className="py-2">{c.membersTitle}</th>
                  <th className="py-2">{c.plan}</th>
                  <th className="py-2">{c.cycle}</th>
                  <th className="py-2">{c.extra}</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.userId} className="border-t border-slate-100">
                    <td className="py-2 font-semibold text-slate-800">{m.label}</td>
                    <td className="py-2 uppercase text-slate-600">{m.planCode}</td>
                    <td className="py-2 font-bold text-slate-900">{m.remaining}</td>
                    <td className="py-2 font-bold text-slate-900">{m.extra}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
};

export default AdminLeadCredits;
